
import {
    StoreEnhancer,
    StoreEnhancerStoreCreator,
    Store,
    Reducer,
    Action,
    Dispatch,
    AnyAction,
    DeepPartial
} from 'redux';
import { ThunkAction } from 'redux-thunk';
import { createActionCreator } from '../actions/createActionCreator';
import { Signal } from '../../utilities/async/Signal';

interface IInternalWaitForNextStoreUpdatePayload {
    signal: Signal<{}>;
}

const internalWaitForNextStoreUpdate =
    createActionCreator<'waitForNextStoreUpdate', IInternalWaitForNextStoreUpdatePayload>('waitForNextStoreUpdate');

const internalForceNextStoreUpdate = createActionCreator<'forceNextStoreUpdate', {}>('forceNextStoreUpdate');

/**
 * @public
 */
export interface INextStoreUpdateContext<S> {
    nextStoreUpdate: Promise<S>;
}

/**
 * Returns an action which, when dispatched, waits for the next store notification
 * from pending updates.
 * @public
 */
export function waitForNextStoreUpdate<S>(): ThunkAction<Promise<S>, S, {}, AnyAction> {
    return async (dispatch: Dispatch<AnyAction>) => {
        const signal = new Signal<{}>();

        dispatch(internalWaitForNextStoreUpdate({
            signal
        }));

        return (await signal.promise) as S;
    };
}

/**
 * Returns and action which, when dispatched, attempts to 'flush' pending store updates
 * and immediately notifiy any subscribers of the changes.
 * @beta
 */
export function forceStoreUpdate<S>(): ThunkAction<void, S, {}, AnyAction> {
    return (dispatch: Dispatch<AnyAction>) => {
        dispatch(internalForceNextStoreUpdate({}));
    };
}

/**
 * @public
 */
export function nextStoreUpdateEnhancer(forceNotify: () => void): StoreEnhancer {
    return (next: StoreEnhancerStoreCreator): StoreEnhancerStoreCreator => {
        return <S, A extends Action = AnyAction>(reducer: Reducer<S, A>, initialState: DeepPartial<S> | undefined): Store<S, A> => {
            const {
                dispatch: nextDispatch,
                ...store
            } = next(reducer, initialState);

            let previousState = store.getState();

            let {
                resolve,
                promise
            } = new Signal<S>();

            store.subscribe(() => {
                const currentResolve = resolve;

                // Reset the signal before resolving the current one.
                // This will ensure all subsequent 'wait for next store update' actions
                // get queued for the one after this.
                ({
                    resolve,
                    promise
                } = new Signal<S>());

                currentResolve(previousState);

                previousState = store.getState();
            });

            const dispatch: typeof nextDispatch = <T extends A>(action: T): T => {
                if (typeof action !== 'function') {
                    if (internalWaitForNextStoreUpdate.matches(action)) {
                        const {
                            payload: {
                                signal
                            }
                        } = action;

                        (async () => {
                            const state = await promise;
                            signal.resolve(state);
                        })();

                        return action;
                    } else if (internalForceNextStoreUpdate.matches(action)) {
                        forceNotify();

                        return action;
                    } else {
                        return nextDispatch(action);
                    }
                } else {
                    return nextDispatch(action);
                }
            };

            return {
                ...store,
                dispatch: dispatch
            };
        };
    };
}
